/**
 * useDashboardFilters Hook (Facade)
 *
 * Orchestrates all filter state for the dashboard with URL synchronization.
 * Coordinates search, severity, range, and product filters through delegation
 * to specialized hooks.
 *
 * This facade provides a unified API for filter management while
 * maintaining separation of concerns through delegation.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useDebounce } from '../core/useDebounce';
import { useRangeFilters, type UseRangeFiltersReturn } from '../filters/useRangeFilters';
import { useProductFilters, type UseProductFiltersReturn } from '../products/useProductFilters';
import {
  parseURLFilters,
  serializeFiltersToURL,
  formatRangeLabel,
  type URLFilterState,
} from '../../types/search';
import type { AppliedFilterPill } from '../../components/search-filter-overlay/AppliedFilterPills';
import type { TrackedProduct, AlertSeverity } from '../../types/dashboard';

/**
 * Input parameters for useDashboardFilters hook
 */
export interface UseDashboardFiltersInput {
  /** Tracked products for product filter options */
  products: TrackedProduct[];
  /** Current selected tab index (for URL sync) */
  selectedTab: number;
}

/**
 * Return type for useDashboardFilters hook
 */
export interface UseDashboardFiltersReturn {
  // Search
  /** Current search query value */
  searchQuery: string;
  /** Set search query value */
  setSearchQuery: (value: string) => void;
  /** Debounced search query for filtering */
  debouncedSearchQuery: string;

  // Severity filter
  /** Selected severity filters */
  severityFilter: AlertSeverity[];
  /** Set severity filter values */
  setSeverityFilter: (value: AlertSeverity[]) => void;

  // Range filters (delegated to useRangeFilters)
  /** Range filter state and handlers */
  rangeFilters: UseRangeFiltersReturn;

  // Product filters (delegated to useProductFilters)
  /** Product filter state and handlers */
  productFilters: UseProductFiltersReturn;

  // Applied filter pills
  /** Pills for displaying active filters */
  appliedFilterPills: AppliedFilterPill[];
  /** Total count of active filters */
  totalFilterCount: number;

  // Clear handlers
  /** Clear all filters across all categories */
  handleClearAllFilters: () => void;
  /** Remove a specific filter by key */
  handleRemoveFilterPill: (key: string) => void;
  /** Clear only alert-related filters (search, severity, range) */
  handleAlertFiltersClearAll: () => void;
  /** Clear search query only */
  handleQueryValueRemove: () => void;
}

/**
 * Facade hook for managing all dashboard filter state
 *
 * Coordinates multiple filter hooks and provides:
 * - URL synchronization for filter persistence
 * - Unified applied filter pills generation
 * - Coordinated clear handlers
 *
 * @param input - Products data and selected tab
 * @returns Unified filter state, handlers, and computed values
 */
export function useDashboardFilters({
  products,
  selectedTab,
}: UseDashboardFiltersInput): UseDashboardFiltersReturn {
  const [searchParams] = useSearchParams();

  // Parse initial filter state from URL
  const initialFilters = useMemo(() => parseURLFilters(searchParams), []);

  // Search state with debouncing
  const [searchQuery, setSearchQuery] = useState(initialFilters.q || '');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Severity filter state
  const [severityFilter, setSeverityFilter] = useState<AlertSeverity[]>(
    (initialFilters.severity || []) as AlertSeverity[]
  );

  // Delegate to range filters hook
  const rangeFilters = useRangeFilters({
    initialDaysMin: initialFilters.daysMin || '',
    initialDaysMax: initialFilters.daysMax || '',
    initialInventoryMin: initialFilters.inventoryMin || '',
    initialInventoryMax: initialFilters.inventoryMax || '',
  });

  // Delegate to product filters hook
  const productFilters = useProductFilters({
    products,
    debouncedSearchQuery,
  });

  // Destructure range values for easier access
  const { daysMin, daysMax, inventoryMin, inventoryMax } = rangeFilters;

  // Sync filter state to URL without triggering React Router navigation
  // Uses window.history.replaceState to avoid showing skeleton on filter/tab changes
  useEffect(() => {
    const newParams = serializeFiltersToURL(
      {
        q: searchQuery || undefined,
        severity: severityFilter,
        daysMin: daysMin || undefined,
        daysMax: daysMax || undefined,
        inventoryMin: inventoryMin || undefined,
        inventoryMax: inventoryMax || undefined,
        tab: selectedTab > 0 ? selectedTab.toString() : undefined,
      },
      searchParams
    );

    const currentParamsStr = searchParams.toString();
    const newParamsStr = newParams.toString();
    if (currentParamsStr !== newParamsStr) {
      // Use replaceState to update URL without triggering navigation
      const newUrl = `${window.location.pathname}${newParamsStr ? `?${newParamsStr}` : ''}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [searchQuery, severityFilter, daysMin, daysMax, inventoryMin, inventoryMax, selectedTab, searchParams]);

  // Clear search query handler
  const handleQueryValueRemove = useCallback(() => setSearchQuery(''), []);

  // Clear alert-specific filters (search, severity, range)
  const handleAlertFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
    setSeverityFilter([]);
    rangeFilters.clearAllFilters();
  }, [handleQueryValueRemove, rangeFilters]);

  // Clear all filters across all categories
  const handleClearAllFilters = useCallback(() => {
    handleQueryValueRemove();
    setSeverityFilter([]);
    rangeFilters.clearAllFilters();
    productFilters.handleFiltersClearAll();
  }, [handleQueryValueRemove, rangeFilters, productFilters]);

  // Total filter count for badge display
  const totalFilterCount = useMemo(() => {
    let count = 0;
    if (severityFilter.length > 0) count++;
    if (daysMin || daysMax) count++;
    if (inventoryMin || inventoryMax) count++;
    if (productFilters.filters.statusFilter.length > 0) count++;
    if (productFilters.filters.productTypeFilter.length > 0) count++;
    if (productFilters.filters.productVendorFilter.length > 0) count++;
    return count;
  }, [severityFilter, daysMin, daysMax, inventoryMin, inventoryMax, productFilters.filters]);

  // Build applied filter pills for inline display
  const appliedFilterPills = useMemo((): AppliedFilterPill[] => {
    const pills: AppliedFilterPill[] = [];

    // Alert filters
    if (severityFilter.length > 0) {
      pills.push({
        key: 'severity',
        label: `Severity: ${severityFilter.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`,
      });
    }
    if (daysMin || daysMax) {
      pills.push({
        key: 'daysUntilStockout',
        label: `Stockout: ${formatRangeLabel(daysMin, daysMax, 'days')}`,
      });
    }
    if (inventoryMin || inventoryMax) {
      pills.push({
        key: 'inventoryLevel',
        label: `Inventory: ${formatRangeLabel(inventoryMin, inventoryMax, 'units')}`,
      });
    }

    // Product filters
    if (productFilters.filters.statusFilter.length > 0) {
      pills.push({
        key: 'status',
        label: `Status: ${productFilters.filters.statusFilter.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}`,
      });
    }
    if (productFilters.filters.productTypeFilter.length > 0) {
      pills.push({
        key: 'productType',
        label: `Type: ${productFilters.filters.productTypeFilter.join(', ')}`,
      });
    }
    if (productFilters.filters.productVendorFilter.length > 0) {
      pills.push({
        key: 'vendor',
        label: `Vendor: ${productFilters.filters.productVendorFilter.join(', ')}`,
      });
    }

    return pills;
  }, [severityFilter, daysMin, daysMax, inventoryMin, inventoryMax, productFilters.filters]);

  // Handle removing individual filter pills
  const handleRemoveFilterPill = useCallback((key: string) => {
    switch (key) {
      case 'severity':
        setSeverityFilter([]);
        break;
      case 'daysUntilStockout':
        rangeFilters.setDaysMin('');
        rangeFilters.setDaysMax('');
        break;
      case 'inventoryLevel':
        rangeFilters.setInventoryMin('');
        rangeFilters.setInventoryMax('');
        break;
      case 'status':
        productFilters.setStatusFilter([]);
        break;
      case 'productType':
        productFilters.setProductTypeFilter([]);
        break;
      case 'vendor':
        productFilters.setProductVendorFilter([]);
        break;
    }
  }, [rangeFilters, productFilters]);

  return {
    // Search
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,

    // Severity
    severityFilter,
    setSeverityFilter,

    // Delegated hooks
    rangeFilters,
    productFilters,

    // Applied filters
    appliedFilterPills,
    totalFilterCount,

    // Clear handlers
    handleClearAllFilters,
    handleRemoveFilterPill,
    handleAlertFiltersClearAll,
    handleQueryValueRemove,
  };
}
